/**
 * Based on FieldFile Component
 */

import mime from 'mime/lite';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { connect } from 'react-redux';

import { showNotification } from 'Store/Notification/Notification.action';
import { EventsType, FieldAttrType, FileType } from 'Type/Field.type';

import FieldFileAdvanced from './FieldFileAdvanced.component';
import { FIELD_FILE_ADVANCED_MAX_ALLOWED_SIZE } from './FieldFileAdvanced.config';

/** @namespace PeggysagePwa/Component/FieldFileAdvanced/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    isMobile: state.ConfigReducer.device.isMobile
});

/** @namespace PeggysagePwa/Component/FieldFileAdvanced/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    showNotification: (type, message, e) => dispatch(showNotification(type, message, e))
});

/** @namespace PeggysagePwa/Component/FieldFileAdvanced/Container */
export class FieldFileAdvancedContainer extends PureComponent {
    static propTypes = {
        // Field attributes
        attr: FieldAttrType.isRequired,
        events: EventsType.isRequired,
        setRef: PropTypes.func.isRequired,
        description: PropTypes.string,
        isMobile: PropTypes.bool.isRequired,
        showNotification: PropTypes.func.isRequired,
        file: FileType,
        fileData: PropTypes.string,
        maxAllowedSize: PropTypes.number,
        isAllowedTypesVisible: PropTypes.bool
    };

    static defaultProps = {
        description: '',
        file: {},
        fileData: null,
        maxAllowedSize: FIELD_FILE_ADVANCED_MAX_ALLOWED_SIZE,
        isAllowedTypesVisible: false
    };

    containerFunctions = {
        setRef: this.setRef.bind(this),
        resetInputFile: this.resetInputFile.bind(this),
        onCaptureButtonClick: this.onCaptureButtonClick.bind(this),
        onUploadButtonClick: this.onUploadButtonClick.bind(this)
    };

    state = {
        isLoading: false,
        file: {},
        fileData: '',
        capture: false
    };

    fieldRef = createRef();

    componentDidMount() {
        this.setFileFromProps();
    }

    componentDidUpdate(prevProps) {
        const { attr: { name } } = this.props;
        const { attr: { name: prevName } } = prevProps;

        if (name !== prevName) {
            this.setState({ isLoading: false });
            this.setFileFromProps();
        }
    }

    // eslint-disable-next-line no-magic-numbers
    b64toBlob(b64Data, contentType = '', sliceSize = 512) {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        // eslint-disable-next-line fp/no-let
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            // eslint-disable-next-line fp/no-let
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
    }

    setFileFromProps() {
        const {
            file,
            file: {
                name: fileName = '',
                type: fileType = ''
            } = {},
            fileData
        } = this.props;

        if (fileName && fileData && this.fieldRef) {
            // Get mime type from filename
            const mimeType = (!fileType || fileType === '') ? mime.getType(fileName) : fileType;

            // Create a Blob from filedata
            const blob = this.b64toBlob(fileData, mimeType);

            // Create a new File object
            const newFile = new File([blob], fileName, {
                type: mimeType
                // lastModified (will be Date.now() because we can't retrieve this info from the data we have here)
            });

            // Create a DataTransfer to get a FileList
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(newFile);
            this.fieldRef.files = dataTransfer.files;

            // Help Safari out
            if (this.fieldRef.webkitEntries.length) {
                this.fieldRef.dataset.file = `${dataTransfer.files[0].name}`;
            }

            // Set data in state for display
            this.setState({
                file: newFile,
                fileData: `data:${mimeType};base64,${fileData}`
            });

            // Trigger onChange event to update parent component
            const event = new Event('change', { bubbles: true });
            this.fieldRef.dispatchEvent(event);
        } else {
            this.setState({
                file: file || {},
                fileData: fileData || ''
            });
        }
    }

    containerProps() {
        const {
            events,
            attr: {
                autoComplete,
                autocomplete,
                ...attr
            } = {},
            description,
            isMobile,
            maxAllowedSize,
            isAllowedTypesVisible
        } = this.props;
        const {
            file, fileData, isLoading, capture
        } = this.state;

        return {
            attr: {
                ...attr,
                autoComplete: autoComplete || autocomplete,
                capture: capture ? 'environment' : false
            },
            description,
            events: {
                ...events,
                onChange: this.onChange.bind(this)
            },
            file,
            fileData,
            isLoading,
            isMobile,
            maxAllowedSize,
            isAllowedTypesVisible
        };
    }

    setRef(elem) {
        const { setRef } = this.props;

        if (typeof setRef === 'function') {
            setRef(elem);
        }

        if (elem && this.fieldRef !== elem) {
            this.fieldRef = elem;
        }
    }

    onChange(event) {
        const {
            events: { onChange } = {},
            attr: { name, accept: allowedTypesAttr = '' } = {},
            maxAllowedSize,
            showNotification
        } = this.props;

        if (this.fieldRef) {
            // const { files } = this.fieldRef;
            const { files } = event.target;

            if (files[0]) {
                const { name: fileName, type: fileType, size: fileSize } = files[0];

                const allowedTypes = allowedTypesAttr.split(',');

                if (!allowedTypes.includes(fileType)) {
                    this.fieldRef.value = null;
                    showNotification('error', __('This type of file is not allowed.'));
                    return;
                }

                if (fileSize > maxAllowedSize) {
                    this.fieldRef.value = null;
                    showNotification('error', __('Your file exceeds the maximum allowed size!'));
                    return;
                }

                this.setState({ isLoading: true });

                const reader = new FileReader();
                reader.onload = () => {
                    this.setState({
                        file: files[0],
                        fileData: reader.result,
                        isLoading: false
                    });
                    // TypeError: can't define property "x": "obj" is not extensible
                    /* this.fieldRef.fileData = JSON.stringify({
                        file_data: reader.result,
                        file_name: name
                    }); */

                    const fileData = JSON.stringify({
                        filename: fileName,
                        content: reader.result.split(',')[1]
                    });

                    if (typeof onChange === 'function') {
                        onChange(event, { fileData, name });
                    }
                };
                reader.onerror = () => {
                    showNotification('error', __('Failed to upload file'));

                    this.setState({
                        file: {},
                        fileData: '',
                        isLoading: false
                    });

                    if (typeof onChange === 'function') {
                        onChange(event, { name });
                    }
                };
                reader.readAsDataURL(files[0]);
            }
        }
    }

    resetInputFile(event) {
        const { attr: { name }, events: { onChange } = {} } = this.props;

        this.setState({
            file: {},
            fileData: ''
        });

        if (this.fieldRef) {
            this.fieldRef.value = null;
        }

        if (typeof onChange === 'function') {
            onChange(event, { name });
        }
    }

    onCaptureButtonClick() {
        this.setState({ capture: true }, () => {
            this.fieldRef.click();
        });
    }

    onUploadButtonClick() {
        this.setState({ capture: false }, () => {
            this.fieldRef.click();
        });
    }

    render() {
        return (
            <FieldFileAdvanced
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(FieldFileAdvancedContainer);
